/**
 * Google Ads & GA4 Conversion Tracking
 *
 * Centralized tracking module for all conversion events.
 * Fires events to both GA4 (analytics) and Google Ads (bid optimization).
 *
 * Usage:
 *   import { trackConversion, CONVERSIONS } from '../services/analytics';
 *   trackConversion(CONVERSIONS.SIGN_UP);
 *   trackConversion(CONVERSIONS.PURCHASE, { value: 29.96, transaction_id: 'abc123' });
 */

// ──────────────────────────────────────────────────────────────────────────────
// Google Ads Conversion ID — configured for AW-860240762
// To add new conversion actions:
// 1. Go to Google Ads → Tools & Settings → Conversions → + New conversion action
// 2. Choose "Website" → "Manual setup"
// 3. Copy the conversion label and add it to the CONVERSIONS object below.
// ──────────────────────────────────────────────────────────────────────────────
const ADS_CONVERSION_ID = 'AW-860240762';

export const CONVERSIONS = {
    /** Fired on PaymentSuccess page when a purchase is confirmed */
    PURCHASE: {
        ads_label: `${ADS_CONVERSION_ID}/rK4WCPLVv_wZEPr2mJoD`,
        ga4_event: 'purchase',
        default_value: 0, // always use dynamic value
        currency: 'EUR',
    },
    /** Fired after successful user registration */
    SIGN_UP: {
        ads_label: `${ADS_CONVERSION_ID}/lfYOCNTP04waEPr2mJoD`,
        ga4_event: 'sign_up',
        default_value: 5.0,
        currency: 'EUR',
    },
    /** Fired when user initiates checkout (clicks "Proceed to Checkout") */
    BEGIN_CHECKOUT: {
        ads_label: `${ADS_CONVERSION_ID}/vPLWCPjCkPgYEPr2mJoD`,
        ga4_event: 'begin_checkout',
        default_value: 2.0,
        currency: 'EUR',
    },
    /** Fired when user submits URL in the TrafficTestWidget */
    GENERATE_LEAD: {
        ads_label: `${ADS_CONVERSION_ID}/_NbMCM6gzusCEPr2mJoD`,
        ga4_event: 'generate_lead',
        default_value: 1.0,
        currency: 'EUR',
    },
    /** Fired on successful login (GA4 signal only, no Ads conversion) */
    LOGIN: {
        ads_label: '',
        ga4_event: 'login',
        default_value: 0,
        currency: 'EUR',
    },
} as const;

type ConversionConfig = (typeof CONVERSIONS)[keyof typeof CONVERSIONS];

interface ConversionOptions {
    value?: number;
    currency?: string;
    transaction_id?: string;
    items?: Array<{
        item_name: string;
        item_category?: string;
        price?: number;
        quantity?: number;
    }>;
}

declare global {
    interface Window {
        gtag: (...args: any[]) => void;
        dataLayer: any[];
    }
}

/**
 * Fire a conversion event to both GA4 and Google Ads.
 *
 * @param conversion - One of the CONVERSIONS.* constants
 * @param options    - Optional overrides for value, currency, transaction_id, items
 */
export function trackConversion(
    conversion: ConversionConfig,
    options: ConversionOptions = {}
): void {
    if (typeof window === 'undefined' || !window.gtag) {
        console.warn('[Analytics] gtag not available — skipping conversion tracking');
        return;
    }

    const value = options.value ?? conversion.default_value;
    const currency = options.currency ?? conversion.currency;

    // 1. Fire GA4 event (always)
    const ga4Params: Record<string, any> = {
        value,
        currency,
    };
    if (options.transaction_id) ga4Params.transaction_id = options.transaction_id;
    if (options.items) ga4Params.items = options.items;

    window.gtag('event', conversion.ga4_event, ga4Params);

    // 2. Fire Google Ads conversion (only if a label is configured)
    if (conversion.ads_label) {
        const adsParams: Record<string, any> = {
            send_to: conversion.ads_label,
            value,
            currency,
        };
        if (options.transaction_id) adsParams.transaction_id = options.transaction_id;

        window.gtag('event', 'conversion', adsParams);
    }

    if (import.meta.env.DEV) {
        console.debug(`[Analytics] Tracked: ${conversion.ga4_event}`, { value, currency, ...options });
    }
}

/**
 * Track a page view (useful for SPA navigation if needed).
 */
export function trackPageView(path: string, title?: string): void {
    if (typeof window === 'undefined' || !window.gtag) return;

    window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title,
    });
}

/**
 * Track a custom GA4 event (e.g. cta_click, wizard_step, faq_click).
 *
 * Usage:
 *   trackEvent('cta_click', { cta_location: 'hero', page: '/pricing' });
 *   trackEvent('wizard_step', { step: 3, step_name: 'Geo' });
 */
export function trackEvent(eventName: string, params?: Record<string, any>): void {
    if (typeof window === 'undefined' || !window.gtag) return;
    window.gtag('event', eventName, params);
}
